import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import BrushIcon from "@mui/icons-material/Brush";
import InsightsIcon from "@mui/icons-material/Insights";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import LinkIcon from "@mui/icons-material/Link";
import CommunityApi from "../../shared/api/CommunityApi";
import GalleryBands from "../../components/GalleryBands/GalleryBands";
import {
  EloEntry,
  PcaData,
  PopularCategory,
  QuestionInfo,
  LeaderboardEntry,
  AnalyticsInsight,
  AnalyticsDeckEntry,
} from "../../shared/interfaces/GameLogicTypes";
import { Languages } from "../../shared/language/languages";
import { getRandomTsarName } from "../../shared/helper/functions";
import Deck from "../../shared/api/DeckApi";
import { publicWebUrl } from "../../shared/runtime";
import {
  ScatterChart as _ScatterChart,
  Scatter as _Scatter,
  XAxis as _XAxis,
  YAxis as _YAxis,
  CartesianGrid as _CartesianGrid,
  Tooltip as _Tooltip,
  ResponsiveContainer as _ResponsiveContainer,
} from "recharts";

// Cast recharts for React 17 compat
const RScatterChart = _ScatterChart as any;
const RScatter = _Scatter as any;
const RXAxis = _XAxis as any;
const RYAxis = _YAxis as any;
const RCartesianGrid = _CartesianGrid as any;
const RTooltip = _Tooltip as any;
const RResponsiveContainer = _ResponsiveContainer as any;

const TAB_KEYS = ["analytics", "leaderboard", "gallery"] as const;
type TabKey = typeof TAB_KEYS[number];

export default function CommunityPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialTab = TAB_KEYS.includes(params.get("tab") as TabKey)
    ? (params.get("tab") as TabKey)
    : "analytics";

  const [tab, setTab] = useState<TabKey>(initialTab);

  const handleTabChange = (_: any, newTab: TabKey) => {
    setTab(newTab);
    history.replace(`/community?tab=${newTab}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 3,
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 3 } }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/")}
              sx={{ color: "text.primary" }}
            >
              {t("leaderboard_back", "Back")}
            </Button>
            <Typography component="h1" variant="h5" fontWeight="bold" sx={{ flex: 1 }}>
              {t("community_title", "Community")}
            </Typography>
          </Stack>

          {/* Tabs */}
          <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="fullWidth"
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab icon={<InsightsIcon />} label={t("community_tab_analytics", "Analytics")} value="analytics" />
              <Tab icon={<EmojiEventsIcon />} label={t("community_tab_leaderboard", "Leaderboard")} value="leaderboard" />
              <Tab icon={<BrushIcon />} label={t("community_tab_gallery", "Gallery")} value="gallery" />
            </Tabs>
          </Paper>

          {/* Tab content */}
          {tab === "analytics" && <AnalyticsTab />}
          {tab === "leaderboard" && <LeaderboardTab />}
          {tab === "gallery" && <GalleryBands />}
        </Stack>
      </Container>
    </Box>
  );
}

// ═══════════════════════════ Analytics Tab ═══════════════════════════

function stripHtml(s: string) { return s.replace(/<[^>]+>/g, ""); }

const RANK_COLORS = ["#FFD700", "#5B8DEF", "#EC5555", "#4CAF50", "#FF9800"];
const INSIGHT_COLORS: Record<string, string> = {
  divided: "#EC5555",
  consensus: "#4CAF50",
  chaos: "#FF9800",
  two_camps: "#5B8DEF",
  predictable_chaos: "#9C27B0",
  hidden_consensus: "#00BCD4",
  context_shift: "#FF5722",
};

// ─── Rank Distribution Bar ───────────────────────────────────────────────────
function RankDistBar({ distribution, highlight }: { distribution: number[]; highlight?: boolean }) {
  const total = distribution.reduce((a, b) => a + b, 0) || 1;
  return (
    <Tooltip
      title={distribution.map((v, i) => `#${i + 1}: ${Math.round((v / total) * 100)}%`).join("  ")}
      arrow
    >
      <Box
        sx={{
          display: "flex",
          width: 80,
          height: 12,
          borderRadius: 1,
          overflow: "hidden",
          border: highlight ? "1.5px solid #EC5555" : "1px solid #e0e0e0",
          flexShrink: 0,
        }}
      >
        {distribution.map((val, i) => (
          <Box
            key={i}
            sx={{
              height: "100%",
              width: `${(val / total) * 100}%`,
              bgcolor: RANK_COLORS[i],
              minWidth: val > 0 ? 2 : 0,
            }}
          />
        ))}
      </Box>
    </Tooltip>
  );
}

// ─── Distribution Explainer Pill ─────────────────────────────────────────────
const EXPLAINER_STATES: { dist: number[]; key: string; fallback: string }[] = [
  { dist: [20, 20, 20, 20, 20], key: "explainer_default", fallback: "Each color = a rank" },
  { dist: [55, 22, 12, 6, 5], key: "explainer_high", fallback: "Highly rated \u00b7 often ranked #1" },
  { dist: [5, 6, 12, 22, 55], key: "explainer_low", fallback: "Low rated \u00b7 often ranked last" },
];

const pulseKeyframes = {
  "@keyframes explainerPulse": {
    "0%, 100%": { transform: "scale(1)", opacity: 0.8, boxShadow: "0 0 6px 2px rgba(255,255,255,0.3)" },
    "50%": { transform: "scale(1.04)", opacity: 1, boxShadow: "0 0 14px 5px rgba(255,255,255,0.6)" },
  },
};

function DistributionExplainer() {
  const { t } = useTranslation();
  const [stateIdx, setStateIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [textOpacity, setTextOpacity] = useState(1);
  const [displayedLabel, setDisplayedLabel] = useState(t(EXPLAINER_STATES[0].key, EXPLAINER_STATES[0].fallback));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idxRef = useRef(0);

  const goToState = (idx: number) => {
    idxRef.current = idx;
    setStateIdx(idx);
    setTextOpacity(0);
    if (fadeRef.current) clearTimeout(fadeRef.current);
    fadeRef.current = setTimeout(() => {
      setDisplayedLabel(t(EXPLAINER_STATES[idx].key, EXPLAINER_STATES[idx].fallback));
      setTextOpacity(1);
    }, 250);
  };

  const onEnter = () => {
    setHovered(true);
    goToState(1);
    intervalRef.current = setInterval(() => {
      const next = idxRef.current === 1 ? 2 : 1;
      goToState(next);
    }, 2000);
  };

  const onLeave = () => {
    setHovered(false);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (fadeRef.current) { clearTimeout(fadeRef.current); fadeRef.current = null; }
    goToState(0);
  };

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (fadeRef.current) clearTimeout(fadeRef.current);
  }, []);

  const cur = EXPLAINER_STATES[stateIdx];
  const total = cur.dist.reduce((a, b) => a + b, 0);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      sx={{ cursor: "pointer", display: { xs: "none", sm: "flex" }, ...pulseKeyframes }}
    >
      <Box sx={{ width: 200, overflow: "hidden", whiteSpace: "nowrap", flexShrink: 0, textAlign: "right" }}>
        <Typography variant="caption" color="text.secondary" sx={{ opacity: textOpacity, transition: "opacity 250ms ease" }}>
          {displayedLabel}
        </Typography>
      </Box>
      <Box sx={{
        display: "flex",
        width: 200,
        height: 40,
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        flexShrink: 0,
        animation: hovered ? "none" : "explainerPulse 1.5s ease-in-out infinite",
      }}>
        {cur.dist.map((val, i) => (
          <Box key={i} sx={{ height: "100%", width: `${(val / total) * 100}%`, bgcolor: RANK_COLORS[i], transition: "width 600ms ease-in-out", minWidth: val > 0 ? 2 : 0 }} />
        ))}
      </Box>
    </Stack>
  );
}

// ─── Insight Story Card ──────────────────────────────────────────────────────
function InsightStoryCard({
  insight,
  onClick,
}: {
  insight: AnalyticsInsight;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const [tsarName, setTsarName] = useState(() => getRandomTsarName() ?? "Alex");
  useEffect(() => {
    const id = setInterval(() => setTsarName(getRandomTsarName() ?? "Alex"), 10000);
    return () => clearInterval(id);
  }, []);
  const renderTsar = (s: string): React.ReactNode => {
    const text = stripHtml(s);
    const parts = text.split("{{tsar}}");
    if (parts.length === 1) return text;
    return (
      <>
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {part}
            {i < parts.length - 1 && (
              <span style={{ color: "#ec7777", fontWeight: "bold" }}>{tsarName}</span>
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  const labelKey = `community_insight_${insight.insightType}` as const;
  const labelMap: Record<string, string> = {
    community_insight_divided: "The Great Divide",
    community_insight_consensus: "Everyone Agrees",
    community_insight_chaos: "Pure Chaos",
    community_insight_two_camps: "Two Camps",
    community_insight_predictable_chaos: "Open Book",
    community_insight_hidden_consensus: "Hidden Depths",
    community_insight_context_shift: "Chameleon",
  };
  const accentColor = INSIGHT_COLORS[insight.insightType] || "#888";

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 6 },
        borderLeft: `4px solid ${accentColor}`,
      }}
      onClick={onClick}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Chip
          label={t(labelKey, labelMap[labelKey] || insight.insightType)}
          size="small"
          sx={{
            bgcolor: accentColor,
            color: "#fff",
            fontWeight: "bold",
            fontSize: "0.7rem",
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {insight.deckName}
        </Typography>
      </Stack>

      {/* Question */}
      <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
        {renderTsar(insight.question)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
        {t("community_insight_players", "{{count}} players", { count: insight.playerCount })}
      </Typography>

      {/* Card rankings */}
      <Box sx={{ mb: 1.5 }}>
        {/* Column headers */}
        <Stack direction="row" spacing={0} sx={{ px: 1, mb: 0.5 }}>
          <Box sx={{ width: 16, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">Answer</Typography>
          </Box>
          <Box sx={{ width: 40, flexShrink: 0, pl: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">Elo</Typography>
          </Box>
          <Box sx={{ width: 80, flexShrink: 0, pl: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">Distribution</Typography>
          </Box>
        </Stack>
        <Stack spacing={0.5}>
          {insight.cards.map((card, i) => {
            const isHighlight = i === insight.highlightIndex;
            return (
              <Stack
                key={card.answerCardId}
                direction="row"
                alignItems="flex-start"
                spacing={0}
                sx={{
                  py: 0.3,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: isHighlight ? "rgba(236,85,85,0.06)" : "transparent",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ width: 16, flexShrink: 0, fontWeight: "bold", color: "text.secondary", pt: 0.3 }}
                >
                  {i + 1}.
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isHighlight ? "bold" : "normal",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {card.answer}
                  </Typography>
                </Box>
                <Box sx={{ width: 40, flexShrink: 0, display: "flex", alignItems: "center", pl: 1, pt: 0.3 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: "bold", flexShrink: 0 }}>
                    {card.elo}
                  </Typography>
                </Box>
                <Box sx={{ width: 80, flexShrink: 0, display: "flex", alignItems: "center", pl: 1, pt: 0.3 }}>
                  <RankDistBar distribution={card.rankDistribution} highlight={isHighlight} />
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 1 }}>
        {renderTsar(insight.description)}
      </Typography>
      <Typography variant="caption" sx={{ color: accentColor, fontWeight: "bold" }}>
        {t("community_insight_see_full", "See full analytics")} →
      </Typography>
    </Paper>
  );
}

// ─── ELO Table (reusable) ────────────────────────────────────────────────────
function EloTable({ eloData, questionLabel }: { eloData: EloEntry[]; questionLabel?: string }) {
  const { t } = useTranslation();
  const history = useHistory();
  if (!eloData || eloData.length === 0) return null;

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("community_analytics_elo_title", "ELO Rankings")}
          </Typography>
          {questionLabel && (
            <Typography variant="caption" color="text.secondary">
              {questionLabel}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          startIcon={<PlayArrowIcon />}
          variant="outlined"
          onClick={() => history.push("/play")}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          Play
        </Button>
      </Stack>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: { xs: 420, sm: "auto" } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Card</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>ELO</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Distribution</TableCell>
              <TableCell sx={{ fontWeight: "bold", display: { xs: "none", sm: "table-cell" } }}>Win Rate</TableCell>
              <TableCell sx={{ fontWeight: "bold", display: { xs: "none", sm: "table-cell" } }}>Matches</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eloData.map((entry, i) => (
              <TableRow key={entry.answerCardId}>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ maxWidth: { xs: 120, sm: 200 }, whiteSpace: "normal", wordBreak: "break-word" }}>
                  {entry.answer}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 50, height: 8, bgcolor: "#eee", borderRadius: 1, overflow: "hidden", display: { xs: "none", sm: "block" } }}>
                      <Box
                        sx={{
                          height: "100%",
                          width: `${Math.max(0, Math.min(100, ((entry.elo - 1000) / 1000) * 100))}%`,
                          bgcolor: "#ec7777",
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                    <Typography variant="body2">{entry.elo}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <RankDistBar distribution={entry.rankDistribution} />
                </TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  {(entry.winRate * 100).toFixed(0)}%
                </TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  {entry.matchCount}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ─── Clustering (k-means with auto k via silhouette) ─────────────────────────

const CLUSTER_COLORS = ["#ec7777", "#5B8DEF", "#4CAF50", "#FF9800", "#9C27B0"];

function dist2(a: { x: number; y: number }, b: { x: number; y: number }) {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
}

function kMeans(points: { x: number; y: number }[], k: number, maxIter = 30): number[] {
  const n = points.length;
  if (n <= k) return points.map((_, i) => i % k);

  // Init centroids via k-means++ seeding
  const centroids: { x: number; y: number }[] = [];
  centroids.push({ ...points[Math.floor(Math.random() * n)] });
  for (let c = 1; c < k; c++) {
    const dists = points.map((p) => Math.min(...centroids.map((cen) => dist2(p, cen))));
    const total = dists.reduce((a, b) => a + b, 0);
    let r = Math.random() * total;
    for (let i = 0; i < n; i++) {
      r -= dists[i];
      if (r <= 0) { centroids.push({ ...points[i] }); break; }
    }
    if (centroids.length === c) centroids.push({ ...points[Math.floor(Math.random() * n)] });
  }

  const labels = new Array(n).fill(0);
  for (let iter = 0; iter < maxIter; iter++) {
    let changed = false;
    for (let i = 0; i < n; i++) {
      let best = 0, bestD = dist2(points[i], centroids[0]);
      for (let c = 1; c < k; c++) {
        const d = dist2(points[i], centroids[c]);
        if (d < bestD) { best = c; bestD = d; }
      }
      if (labels[i] !== best) { labels[i] = best; changed = true; }
    }
    if (!changed) break;
    for (let c = 0; c < k; c++) {
      let sx = 0, sy = 0, cnt = 0;
      for (let i = 0; i < n; i++) {
        if (labels[i] === c) { sx += points[i].x; sy += points[i].y; cnt++; }
      }
      if (cnt > 0) { centroids[c] = { x: sx / cnt, y: sy / cnt }; }
    }
  }
  return labels;
}

function silhouetteScore(points: { x: number; y: number }[], labels: number[]): number {
  const n = points.length;
  const k = Math.max(...labels) + 1;
  if (k <= 1 || n <= k) return -1;
  let totalS = 0;
  for (let i = 0; i < n; i++) {
    const myCluster = labels[i];
    let intraSum = 0, intraCount = 0;
    const interSums: number[] = new Array(k).fill(0);
    const interCounts: number[] = new Array(k).fill(0);
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const d = Math.sqrt(dist2(points[i], points[j]));
      if (labels[j] === myCluster) { intraSum += d; intraCount++; }
      else { interSums[labels[j]] += d; interCounts[labels[j]]++; }
    }
    const a = intraCount > 0 ? intraSum / intraCount : 0;
    let b = Infinity;
    for (let c = 0; c < k; c++) {
      if (c !== myCluster && interCounts[c] > 0) {
        b = Math.min(b, interSums[c] / interCounts[c]);
      }
    }
    if (b === Infinity) b = 0;
    const denom = Math.max(a, b);
    totalS += denom > 0 ? (b - a) / denom : 0;
  }
  return totalS / n;
}

function autoClusters(points: { x: number; y: number }[]): number[] {
  if (points.length < 3) return points.map(() => 0);
  let bestLabels = points.map(() => 0);
  let bestScore = -1;
  const maxK = Math.min(5, Math.floor(points.length / 2));
  if (maxK < 2) return points.map(() => 0);
  // Run each k multiple times to avoid bad init
  for (let k = 2; k <= maxK; k++) {
    for (let trial = 0; trial < 5; trial++) {
      const labels = kMeans(points, k);
      const score = silhouetteScore(points, labels);
      if (score > bestScore) { bestScore = score; bestLabels = labels; }
    }
  }
  // Lower threshold for small sets (5 cards), stricter for larger
  const threshold = points.length <= 6 ? 0.1 : 0.2;
  if (bestScore < threshold) return points.map(() => 0);
  return bestLabels;
}

// ─── PCA Chart (reusable) ────────────────────────────────────────────────────
function PcaChart({ pcaData, eloData }: { pcaData: PcaData; eloData: EloEntry[] }) {
  const { t } = useTranslation();
  const [pcaLinkCopied, setPcaLinkCopied] = useState(false);

  // Hooks must be called unconditionally — compute clustering before early return
  const { points, clustered, k } = React.useMemo(() => {
    if (!pcaData || pcaData.components.length < 2 || !eloData) {
      return { points: [] as { x: number; y: number; label: string; cardId: number }[], clustered: {} as Record<number, { x: number; y: number; label: string; cardId: number }[]>, k: 0 };
    }
    const pts = pcaData.cardIds.map((cardId: number, idx: number) => {
      const card = eloData.find((e) => e.answerCardId === cardId);
      return {
        x: pcaData.components[0][idx],
        y: pcaData.components[1][idx],
        label: card?.answer ?? `Card #${cardId}`,
        cardId,
      };
    });
    const labels = autoClusters(pts);
    const numK = labels.length > 0 ? Math.max(...labels) + 1 : 1;
    const groups: Record<number, typeof pts> = {};
    pts.forEach((p, i) => {
      const c = labels[i];
      if (!groups[c]) groups[c] = [];
      groups[c].push(p);
    });
    return { points: pts, clustered: groups, k: numK };
  }, [pcaData, eloData]);

  if (!pcaData || pcaData.components.length < 2 || !eloData || points.length === 0) return null;

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t("community_analytics_pca_title", "Opinion Groups")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t("community_analytics_pca_subtitle", "How players cluster by preference — {{pc1}}% + {{pc2}}% of variance explained", {
              pc1: (pcaData.explainedVariance[0] * 100).toFixed(1),
              pc2: (pcaData.explainedVariance[1] * 100).toFixed(1),
            })}
            {" — "}
            {pcaData.playerCount} players, {pcaData.totalComparisons} comparisons
            {k > 1 ? ` — ${k} groups found` : ""}
          </Typography>
        </Box>
        <Tooltip title={pcaLinkCopied ? "Copied!" : "Share link"} placement="top">
          <IconButton
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(publicWebUrl(window.location.pathname + window.location.search)).then(() => {
                setPcaLinkCopied(true);
                setTimeout(() => setPcaLinkCopied(false), 2000);
              });
            }}
            sx={{ color: pcaLinkCopied ? "#4caf50" : "text.secondary", flexShrink: 0 }}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ width: "100%", height: 300 }}>
        <RResponsiveContainer width="100%" height="100%">
          <RScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <RCartesianGrid strokeDasharray="3 3" />
            <RXAxis dataKey="x" name="PC1" type="number" />
            <RYAxis dataKey="y" name="PC2" type="number" />
            <RTooltip
              content={({ payload }: any) => {
                if (!payload || payload.length === 0) return null;
                const d = payload[0]?.payload;
                return (
                  <Paper sx={{ p: 1 }}>
                    <Typography variant="caption" fontWeight="bold">{d?.label}</Typography>
                    <Typography variant="caption" display="block">
                      ({d?.x?.toFixed(3)}, {d?.y?.toFixed(3)})
                    </Typography>
                  </Paper>
                );
              }}
            />
            {Object.entries(clustered).map(([clusterIdx, clusterPoints]) => (
              <RScatter
                key={clusterIdx}
                name={k > 1 ? `Group ${Number(clusterIdx) + 1}` : "Cards"}
                data={clusterPoints}
                fill={CLUSTER_COLORS[Number(clusterIdx) % CLUSTER_COLORS.length]}
              />
            ))}
          </RScatterChart>
        </RResponsiveContainer>
      </Box>
    </Paper>
  );
}

// ─── Deck Drill-Down (Section 2 expanded content) ───────────────────────────
function DeckDrillDown({ deckId, initialCategoryName, initialQuestionCardId }: {
  deckId: string;
  initialCategoryName?: string;
  initialQuestionCardId?: number;
}) {
  const history = useHistory();
  const [categories, setCategories] = useState<PopularCategory[]>([]);
  const [selectedCat, setSelectedCat] = useState<PopularCategory | null>(null);
  const [questions, setQuestions] = useState<QuestionInfo[]>([]);
  const [selectedQ, setSelectedQ] = useState<QuestionInfo | null>(null);
  const [eloData, setEloData] = useState<EloEntry[] | null>(null);
  const [pcaData, setPcaData] = useState<PcaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const pushUrl = (categoryName?: string, questionCardId?: number) => {
    const p = new URLSearchParams({ tab: "analytics", deck: deckId });
    if (categoryName) p.set("category", categoryName);
    if (questionCardId !== undefined) p.set("question", String(questionCardId));
    history.replace(`/community?${p.toString()}`);
  };

  useEffect(() => {
    setLoading(true);
    CommunityApi.getPopularCategories(deckId).then((cats) => {
      setCategories(cats);
      setLoading(false);
      if (cats.length === 0) return;
      // Auto-select: prefer the insight's category, else first
      const target = initialCategoryName
        ? cats.find((c) => c.categoryName === initialCategoryName) ?? cats[0]
        : cats[0];
      selectCat(target, initialQuestionCardId);
    });
  }, [deckId]);

  const selectCat = async (cat: PopularCategory, targetQuestionCardId?: number) => {
    setSelectedCat(cat);
    setSelectedQ(null);
    setEloData(null);
    setPcaData(null);
    pushUrl(cat.categoryName);
    const qs = await CommunityApi.getQuestions(cat.deckId, cat.categoryName);
    setQuestions(qs);
    if (qs.length === 0) return;
    const target = targetQuestionCardId
      ? qs.find((q) => q.questionCardId === targetQuestionCardId) ?? qs[0]
      : qs[0];
    selectQ(cat, target);
  };

  const selectQ = async (cat: PopularCategory, q: QuestionInfo) => {
    setSelectedQ(q);
    setLoadingDetail(true);
    pushUrl(cat.categoryName, q.questionCardId);
    const [elo, pca] = await Promise.all([
      CommunityApi.getAnalytics(cat.deckId, cat.categoryName, q.questionCardId),
      CommunityApi.getPca(cat.deckId, cat.categoryName, q.questionCardId),
    ]);
    setEloData(elo);
    setPcaData(pca);
    setLoadingDetail(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicWebUrl(window.location.pathname + window.location.search)).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  if (loading) return <Skeleton variant="rounded" height={60} sx={{ borderRadius: 2, mx: 2, my: 1 }} />;
  if (categories.length === 0) return (
    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>No categories with data</Typography>
  );

  return (
    <Stack spacing={1.5} sx={{ px: { xs: 1, sm: 2 }, pb: 2, pt: 1 }}>
      {/* Category chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {categories.map((cat) => {
          const sel = selectedCat?.categoryName === cat.categoryName;
          return (
            <Chip
              key={cat.categoryName}
              label={cat.categoryName}
              size="small"
              variant={sel ? "filled" : "outlined"}
              color={sel ? "primary" : "default"}
              onClick={() => selectCat(cat)}
            />
          );
        })}
      </Box>

      {/* Question list */}
      {questions.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          {questions.map((q) => {
            const sel = selectedQ?.questionCardId === q.questionCardId;
            return (
              <Box
                key={q.questionCardId}
                onClick={() => selectedCat && selectQ(selectedCat, q)}
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  border: sel ? "2px solid #ec7777" : "2px solid transparent",
                  bgcolor: sel ? "rgba(236,119,119,0.06)" : "transparent",
                  transition: "all 0.15s ease",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={sel ? "bold" : "normal"} sx={{ color: sel ? "#ec7777" : "text.primary" }}>
                      {stripHtml(q.forTsar)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{q.cardCount} cards</Typography>
                  </Box>
                  {sel && (
                    <Tooltip title={linkCopied ? "Copied!" : "Copy link"} placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleCopyLink(); }}
                        sx={{ color: linkCopied ? "#4caf50" : "text.secondary", flexShrink: 0, mt: -0.5 }}
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Detail */}
      {loadingDetail ? (
        <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={24} /></Stack>
      ) : (
        <>
          {eloData && <EloTable eloData={eloData} questionLabel={selectedQ ? stripHtml(selectedQ.forTsar) : undefined} />}
          {pcaData && eloData && <PcaChart pcaData={pcaData} eloData={eloData} />}
        </>
      )}
    </Stack>
  );
}

// ─── Main Analytics Tab ──────────────────────────────────────────────────────
function AnalyticsTab() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  // Section 1: Insights
  const [insights, setInsights] = useState<AnalyticsInsight[]>([]);
  const [loadingInsights, setLoadingInsights] = useState(true);

  // Section 2: Decks (top 25 + search)
  const [decks, setDecks] = useState<AnalyticsDeckEntry[]>([]);
  const [expandedDeck, setExpandedDeck] = useState<string | null>(null);
  const [loadingDecks, setLoadingDecks] = useState(true);
  const [deckSearch, setDeckSearch] = useState("");
  const [searchResults, setSearchResults] = useState<AnalyticsDeckEntry[] | null>(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [insightTarget, setInsightTarget] = useState<{ deckId: string; categoryName: string; questionCardId: number } | null>(null);

  // Ref for scroll-to-section from insight click
  const exploreByDeckRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Read URL params — if deck/category/question are present, auto-expand on load
    const urlParams = new URLSearchParams(location.search);
    const urlDeck = urlParams.get("deck");
    const urlCategory = urlParams.get("category");
    const urlQuestion = urlParams.get("question");

    CommunityApi.getAnalyticsInsights().then((data) => {
      setInsights(data);
      setLoadingInsights(false);
    });
    CommunityApi.getAnalyticsDecks(25).then((data) => {
      setDecks(data);
      setLoadingDecks(false);

      // Auto-expand from URL params once deck list is loaded
      if (urlDeck) {
        const target = urlCategory && urlQuestion
          ? { deckId: urlDeck, categoryName: urlCategory, questionCardId: Number(urlQuestion) }
          : urlCategory
          ? { deckId: urlDeck, categoryName: urlCategory, questionCardId: 0 }
          : null;
        if (target) setInsightTarget(target);
        setExpandedDeck(urlDeck);

        // If not in top-25, search for it
        const inList = data.some((d) => d.deckId === urlDeck);
        if (!inList) {
          // Search by deck ID — may not find exact match but best effort
          CommunityApi.getAnalyticsDecks(50, urlDeck).then((results) => {
            setSearchResults(results);
          });
        }

        setTimeout(() => {
          const el = document.querySelector(`[data-deck-id="${urlDeck}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          else exploreByDeckRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
      }
    });
  }, []);

  // Debounced deck search
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleDeckSearch = (query: string) => {
    setDeckSearch(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) {
      setSearchResults(null);
      setLoadingSearch(false);
      return;
    }
    setLoadingSearch(true);
    searchTimerRef.current = setTimeout(() => {
      CommunityApi.getAnalyticsDecks(50, query.trim()).then((results) => {
        setSearchResults(results);
        setLoadingSearch(false);
      });
    }, 300);
  };

  // Click insight → expand matching deck accordion and auto-select category + question
  const handleInsightClick = (insight: AnalyticsInsight) => {
    setInsightTarget({ deckId: insight.deckId, categoryName: insight.categoryName, questionCardId: insight.questionCardId });
    setExpandedDeck(insight.deckId);

    // If this deck isn't in the top-25 list, search for it so it appears
    const inTopList = decks.some((d) => d.deckId === insight.deckId);
    const inSearchResults = (searchResults || []).some((d) => d.deckId === insight.deckId);
    if (!inTopList && !inSearchResults) {
      handleDeckSearch(insight.deckName);
    }

    // Scroll to the specific deck accordion after React re-renders
    setTimeout(() => {
      const el = document.querySelector(`[data-deck-id="${insight.deckId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        exploreByDeckRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 250);
  };

  const allEmpty = !loadingInsights && !loadingDecks
    && insights.length === 0 && decks.length === 0;

  if (allEmpty) {
    return (
      <Paper sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
        <InsightsIcon sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
        <Typography variant="h6" color="text.secondary">
          {t("community_analytics_empty", "No analytics data available yet. Play some games first!")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {/* ═══ Section 1: Insight Stories ═══ */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
              {t("community_insights_title", "Interesting Finds")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("community_insights_subtitle", "Stories from how players ranked cards")}
            </Typography>
          </Box>
          <DistributionExplainer />
        </Stack>

        {loadingInsights ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
          </Stack>
        ) : insights.length === 0 ? (
          <Paper sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Not enough data for insights yet — keep playing!
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            {insights.map((ins) => (
              <InsightStoryCard
                key={`${ins.deckId}-${ins.categoryName}-${ins.questionCardId}`}
                insight={ins}
                onClick={() => handleInsightClick(ins)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ═══ Section 2: Explore by Deck ═══ */}
      <Box ref={exploreByDeckRef}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
          {t("community_decks_title", "Explore by Deck")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("community_decks_subtitle", "Decks with analytics data — click to explore")}
        </Typography>

        {loadingDecks ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: 2 }} />)}
          </Box>
        ) : decks.length === 0 ? (
          <Paper sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">No decks with analytics data yet</Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {/* Top 25 most played */}
            <Stack spacing={1}>
              {decks.map((deck) => {
                const isExpanded = expandedDeck === deck.deckId;
                return (
                  <Paper key={deck.deckId} data-deck-id={deck.deckId} sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <Box
                      onClick={() => {
                          const next = isExpanded ? null : deck.deckId;
                          setExpandedDeck(next);
                          setInsightTarget(null);
                          if (next) {
                            const p = new URLSearchParams({ tab: "analytics", deck: next });
                            history.replace(`/community?${p.toString()}`);
                          } else {
                            history.replace("/community?tab=analytics");
                          }
                        }}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        transition: "background 0.15s",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                      }}
                    >
                      <StorageIcon sx={{ color: "#ec7777", fontSize: 28 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                          {deck.deckName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {deck.deckCreator ? `by ${deck.deckCreator} · ` : ""}
                          {t("community_decks_questions", "{{count}} questions", { count: deck.questionCount })}
                          {" · "}
                          {t("community_decks_categories", "{{count}} categories", { count: deck.categoryCount })}
                          {deck.deckLanguage ? ` · ${deck.deckLanguage}` : ""}
                        </Typography>
                      </Box>
                      <IconButton size="small" sx={{ transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                        <ExpandMoreIcon />
                      </IconButton>
                    </Box>
                    <Collapse in={isExpanded}>
                      {isExpanded && (
                        <DeckDrillDown
                          deckId={deck.deckId}
                          initialCategoryName={insightTarget?.deckId === deck.deckId ? insightTarget.categoryName : undefined}
                          initialQuestionCardId={insightTarget?.deckId === deck.deckId ? insightTarget.questionCardId : undefined}
                        />
                      )}
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>

            {/* Search box */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                {t("community_decks_search_title", "Search for a deck")}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={t("community_decks_search_placeholder", "Search by deck name or creator...") as string}
                value={deckSearch}
                onChange={(e) => handleDeckSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: searchResults || loadingSearch ? 1.5 : 0 }}
              />
              {loadingSearch && (
                <Stack alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={20} />
                </Stack>
              )}
              {searchResults && !loadingSearch && (
                searchResults.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: "center" }}>
                    {t("community_decks_search_empty", "No decks found matching your search")}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {searchResults.map((deck) => {
                      const isExpanded = expandedDeck === deck.deckId;
                      return (
                        <Paper key={deck.deckId} data-deck-id={deck.deckId} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                          <Box
                            onClick={() => {
                          const next = isExpanded ? null : deck.deckId;
                          setExpandedDeck(next);
                          setInsightTarget(null);
                          if (next) {
                            const p = new URLSearchParams({ tab: "analytics", deck: next });
                            history.replace(`/community?${p.toString()}`);
                          } else {
                            history.replace("/community?tab=analytics");
                          }
                        }}
                            sx={{
                              p: 1.5,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              transition: "background 0.15s",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                            }}
                          >
                            <StorageIcon sx={{ color: "#ec7777", fontSize: 24 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                {deck.deckName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {deck.deckCreator ? `by ${deck.deckCreator} · ` : ""}
                                {deck.questionCount} questions · {deck.categoryCount} categories
                                {deck.deckLanguage ? ` · ${deck.deckLanguage}` : ""}
                              </Typography>
                            </Box>
                            <IconButton size="small" sx={{ transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                              <ExpandMoreIcon />
                            </IconButton>
                          </Box>
                          <Collapse in={isExpanded}>
                            {isExpanded && (
                              <DeckDrillDown
                                deckId={deck.deckId}
                                initialCategoryName={insightTarget?.deckId === deck.deckId ? insightTarget.categoryName : undefined}
                                initialQuestionCardId={insightTarget?.deckId === deck.deckId ? insightTarget.questionCardId : undefined}
                              />
                            )}
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Stack>
                )
              )}
            </Paper>
          </Stack>
        )}
      </Box>

    </Stack>
  );
}

// ═══════════════════════════ Leaderboard Tab ═══════════════════════════

const PAGE_SIZE = 50;
const MAX_ENTRIES = 100;

function LeaderboardTab() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const initialLanguage = params.get("language") || "";

  const [language, setLanguage] = useState<string>(initialLanguage);
  const [creators, setCreators] = useState<LeaderboardEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchLeaderboard = async (lang: string, offset: number, append: boolean) => {
    if (offset >= MAX_ENTRIES) return;
    const remaining = MAX_ENTRIES - offset;
    const limit = Math.min(PAGE_SIZE, remaining);

    if (offset === 0) setLoading(true);
    else setLoadingMore(true);

    try {
      const res = await Deck.getLeaderboard(lang || undefined, limit, offset);
      if (res) {
        setCreators((prev) => (append ? [...prev, ...res.creators] : res.creators));
        setTotal(res.total);
      }
    } catch (e) {
      console.error("Failed to load leaderboard", e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setCreators([]);
    fetchLeaderboard(language, 0, false);
  }, [language]);

  const handleLanguageChange = (langId: string) => {
    setLanguage(langId === language ? "" : langId);
  };

  const languageEntries = Object.values(Languages);

  return (
    <Stack spacing={2}>
      {/* Language filter */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            label={t("leaderboard_global")}
            variant={language === "" ? "filled" : "outlined"}
            color={language === "" ? "primary" : "default"}
            onClick={() => handleLanguageChange("")}
          />
          {languageEntries.map((lang) => (
            <Chip
              key={lang.id}
              label={lang.name}
              variant={language === lang.id ? "filled" : "outlined"}
              color={language === lang.id ? "primary" : "default"}
              onClick={() => handleLanguageChange(lang.id)}
            />
          ))}
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
        ) : creators.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
            {t("leaderboard_empty")}
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ tableLayout: "auto", minWidth: 0 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", px: { xs: 1, sm: 2 }, width: "3em" }}>#</TableCell>
                    <TableCell sx={{ fontWeight: "bold", px: { xs: 1, sm: 2 } }}>{t("leaderboard_creator")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold", px: { xs: 1, sm: 2 } }}>{t("leaderboard_plays")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold", px: { xs: 1, sm: 2 } }}>{t("leaderboard_decks")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creators.map((creator, i) => (
                    <TableRow
                      key={creator.name + i}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => history.push(`/creator/${encodeURIComponent(creator.name)}`)}
                    >
                      <TableCell sx={{ px: { xs: 1, sm: 2 } }}>{creator.rank}</TableCell>
                      <TableCell sx={{ px: { xs: 1, sm: 2 }, maxWidth: { xs: 120, sm: "none" }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Typography
                          noWrap
                          sx={{
                            fontWeight: creator.rank <= 3 ? "bold" : "normal",
                            color: creator.rank === 1 ? "#ffc107" : creator.rank === 2 ? "#aaa" : creator.rank === 3 ? "#cd7f32" : "inherit",
                          }}
                        >
                          {creator.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ px: { xs: 1, sm: 2 } }}>{creator.playCount}</TableCell>
                      <TableCell align="right" sx={{ px: { xs: 1, sm: 2 } }}>{creator.deckCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {creators.length < total && creators.length < MAX_ENTRIES && (
              <Stack alignItems="center" sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => fetchLeaderboard(language, creators.length, true)} disabled={loadingMore}>
                  {loadingMore ? <CircularProgress size={20} /> : t("leaderboard_load_more")}
                </Button>
              </Stack>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
}
